// ─── GOOGLE CALENDAR OAUTH (Google Identity Services) ─────────────────────────
// Decoupled from Firebase Auth on purpose: Firebase's signInWithPopup never
// hands back a Google refresh_token (client-only implicit flow), so it can
// never renew the Calendar access token without a full interactive consent
// screen. GIS's token client supports a best-effort *silent* renewal
// (prompt: '') when the browser still holds an active Google session and
// prior consent — no backend/refresh_token needed. Firebase Auth continues
// to own "who is this user" (email/profile) separately in firebase.js.

// ─── MIGRATION IN PROGRESS (Phase 02) ────────────────────────────────────────
// The silent-renewal path below is the LEGACY flow. It stays until the OAuth
// consent screen is out of "Testing" (a refresh token issued in Testing state
// expires after 7 days, which would make server-side refresh worse than this),
// and stays one release beyond that as the rollback path.
//
// Flip REACT_APP_SERVER_SIDE_GCAL_AUTH=true to use the authorization-code flow
// instead: the browser hands a one-time code to the exchangeOAuthCode function,
// which holds the refresh token and vends short-lived access tokens. See
// functions/index.js.
export const SERVER_SIDE_AUTH = process.env.REACT_APP_SERVER_SIDE_GCAL_AUTH === 'true';

// Narrowed from the single broad 'auth/calendar' scope: Grove lists calendars
// (/users/me/calendarList) and reads/writes events, and nothing else. A tighter
// scope reads better in OAuth verification and to other families. Keep in sync
// with the scopes recorded on the connection doc in functions/index.js.
export const GCAL_SCOPES = [
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/calendar.calendarlist.readonly',
].join(' ');

const LEGACY_SCOPE = 'https://www.googleapis.com/auth/calendar';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const SILENT_TIMEOUT_MS = 4_000;

let scriptLoadPromise = null;
let tokenClient = null;

function loadGisScript() {
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) { resolve(); return; }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

// One token client for the app's lifetime — GIS docs recommend reusing a
// single instance rather than re-initializing per request.
async function getTokenClient() {
  await loadGisScript();
  if (tokenClient) return tokenClient;
  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    scope: LEGACY_SCOPE,
    callback: () => {}, // overridden per-request below
  });
  return tokenClient;
}

// interactive:true  → shows Google's consent UI (first-ever grant, or the
//                      user explicitly clicked "Reconnect").
// interactive:false → silent attempt only (prompt: ''); raced against a hard
//                      timeout so a stuck/blocked silent request can never
//                      hang the caller — this app already got burned once by
//                      a silent-refresh attempt that hung the UI (see
//                      firebase.js history), so the timeout is load-bearing,
//                      not optional polish.
export async function requestGCalToken({ interactive }) {
  const client = await getTokenClient();

  const attempt = new Promise((resolve, reject) => {
    client.callback = (response) => {
      if (response?.error) reject(new Error(response.error));
      else if (response?.access_token) resolve(response.access_token);
      else reject(new Error('No access token in GIS response'));
    };
    try {
      client.requestAccessToken({ prompt: interactive ? '' : 'none' });
    } catch (err) {
      reject(err);
    }
  });

  if (interactive) return attempt;

  return Promise.race([
    attempt,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Silent GCal token request timed out')), SILENT_TIMEOUT_MS)
    ),
  ]);
}

// ─── AUTHORIZATION CODE FLOW (server-side auth) ──────────────────────────────
// initCodeClient in popup mode returns a one-time authorization code with no
// redirect page to host. The code is worthless on its own — it can only be
// exchanged with the client secret, which lives in Secret Manager and never
// reaches the browser.
//
// access_type:'offline' + prompt:'consent' are both required to reliably get a
// refresh_token back. prompt:'consent' means the user sees the consent screen
// on every connect, which is correct here: connecting is a deliberate,
// click-initiated act that happens once, not the silent background renewal the
// legacy path attempts.

let codeClient = null;

export async function requestGCalAuthCode() {
  await loadGisScript();

  return new Promise((resolve, reject) => {
    if (!codeClient) {
      codeClient = window.google.accounts.oauth2.initCodeClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: GCAL_SCOPES,
        ux_mode: 'popup',
        access_type: 'offline',
        prompt: 'consent',
        callback: () => {},        // overridden per-request below
      });
    }
    codeClient.callback = (response) => {
      if (response?.error) reject(new Error(response.error));
      else if (response?.code) resolve(response.code);
      else reject(new Error('No authorization code in GIS response'));
    };
    // Fires when the user closes the popup or Google refuses to open it —
    // without this the promise would hang exactly the way the pre-2e71a94
    // signInWithPopup path did.
    codeClient.error_callback = (err) => {
      reject(new Error(err?.type === 'popup_closed' ? 'Consent window closed' : (err?.type || 'Consent failed')));
    };
    try {
      codeClient.requestCode();
    } catch (err) {
      reject(err);
    }
  });
}
